import { get, merge, has } from 'lodash';
import {
  VEGA_SPEC_AXIS_SETTINGS,
  VEGA_SPEC_LEGEND_SETTINGS,
  CHART_COLORS,
} from 'constants/chart-spec';
import { CHART_CATEGORIES } from 'constants/chart-categories';
import { generateVegaLabelTextExpression } from 'utils/chart/vega-expressions';
import { generateVegaTooltip } from 'utils/chart/tooltips';
import {
  generateVegaSortParams,
  generateVegaTypeCoercionTransforms,
  generateVegaSortingAndLimitingTransforms,
} from 'utils/chart/transforms';
import { getScaleMinMaxProps } from 'utils/chart/scale-min-max';

export default {
  name: 'Grouped Column',
  displayName: 'Grouped',
  order: 0,
  flavour: 'discrete',
  category: CHART_CATEGORIES.COLUMN,
  channels: [
    {
      name: 'x',
      displayName: 'X Axis',
      required: false,
      channelType: 'category',
      intent: 'category-axis',
    },
    {
      name: 'y',
      displayName: 'Y Axis',
      required: true,
      channelType: 'aggregation',
      intent: 'value-axis',
      multipleSeries: true,
    },
    {
      name: 'color',
      displayName: 'Series',
      required: false,
      channelType: 'category',
      intent: 'series',
    },
  ],
  iconValue: 'column-grouped',
  specFn: (encodings, customisations) => {
    const sortParams = generateVegaSortParams(encodings);
    const logScaleOnValueAxis = get(
      customisations.axes,
      'y.logScale.enabled',
      false
    );
    const valueAxisProps = getScaleMinMaxProps(
      customisations,
      'y',
      logScaleOnValueAxis
    );
    return {
      autosize: {
        type: 'fit',
        contains: 'padding',
      },
      style: 'cell',
      data: [
        {
          name: 'transformedData',
          source: 'values',
          transform: [
            ...generateVegaTypeCoercionTransforms('y'),
            ...generateVegaSortingAndLimitingTransforms(encodings),
          ],
        },
      ],
      scales: [
        {
          name: 'x',
          type: 'band',
          domain: { data: 'transformedData', field: 'x', sort: sortParams.x },
          range: 'width',
          padding: 0.2,
        },
        {
          name: 'y',
          type: logScaleOnValueAxis ? 'log' : 'linear',
          domain: { data: 'transformedData', field: 'y' },
          range: 'height',
          round: true,
          zero: !logScaleOnValueAxis,
          ...valueAxisProps,
        },
        {
          name: 'color',
          type: 'ordinal',
          domain: {
            data: 'transformedData',
            field: 'color',
            sort: sortParams.color,
          },
          range:
            get(customisations.options, 'colorDiscrete.value') || CHART_COLORS,
        },
      ],

      axes: [
        merge({}, VEGA_SPEC_AXIS_SETTINGS, {
          orient: 'bottom',
          scale: 'x',
          title: get(encodings, 'x.axis.title', ''),
          encode: {
            labels: {
              update: {
                angle: { value: 270 },
                align: { value: 'right' },
                baseline: { value: 'middle' },
                text: {
                  signal: generateVegaLabelTextExpression(
                    encodings.x,
                    customisations.channels.x
                  ),
                },
              },
            },
          },
        }),
        merge({}, VEGA_SPEC_AXIS_SETTINGS, {
          orient: 'left',
          scale: 'y',
          grid: true,
          title: get(encodings, 'y.axis.title', ''),
          encode: {
            labels: {
              update: {
                text: {
                  signal: generateVegaLabelTextExpression(
                    encodings.y,
                    customisations.channels.y,
                    { withDecimals: false }
                  ),
                },
              },
            },
          },
        }),
      ],

      legends: !has(encodings, 'color.legend.title')
        ? undefined
        : [
            merge({}, VEGA_SPEC_LEGEND_SETTINGS, {
              fill: 'color',
              title: get(encodings, 'color.legend.title', ''),
              encode: {
                labels: {
                  update: {
                    text: {
                      signal: generateVegaLabelTextExpression(
                        encodings.color,
                        customisations.channels.color
                      ),
                    },
                  },
                },
              },
            }),
          ],

      marks: [
        {
          type: 'group',

          from: {
            facet: {
              data: 'transformedData',
              name: 'facet',
              groupby: 'x',
            },
          },

          encode: {
            enter: {
              x: { scale: 'x', field: 'x' },
            },
          },

          signals: [{ name: 'width', update: "bandwidth('x')" }],

          scales: [
            {
              name: 'pos',
              type: 'band',
              range: 'width',
              domain: {
                data: 'transformedData',
                field: 'color',
                sort: sortParams.color,
              },
            },
          ],

          marks: [
            {
              name: 'bars',
              from: { data: 'facet' },
              type: 'rect',
              encode: {
                enter: {
                  x: { scale: 'pos', field: 'color' },
                  y: { scale: 'y', field: 'y' },
                  y2: {
                    scale: 'y',
                    value: logScaleOnValueAxis ? valueAxisProps.domainMin : 0,
                  },
                  width: { scale: 'pos', band: 1 },
                  fill: { scale: 'color', field: 'color' },
                  tooltip: {
                    signal: generateVegaTooltip(encodings, customisations),
                  },
                },
              },
            },
            ...(!get(customisations.options, 'dataValueLabels.enabled')
              ? []
              : [
                  {
                    type: 'text',
                    name: 'valueLabels',
                    from: { data: 'facet' },
                    encode: {
                      enter: {
                        text: {
                          signal: generateVegaLabelTextExpression(
                            encodings.y,
                            customisations.channels.y,
                            { field: 'y' }
                          ),
                        },
                        x: {
                          scale: 'pos',
                          field: 'color',
                          offset: { signal: "bandwidth('pos') / 2" },
                        },
                        y: { scale: 'y', field: 'y' },
                        align: { value: 'center' },
                        dy: { value: -3 },
                      },
                    },
                  },
                ]),
          ],
        },
      ],
    };
  },
};
